<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useDateTime, can } from '@/composables';
import { PERMISSIONS } from '@/enum';
import useUserTreatmentPlansStore from '@/store/People/Person/TreatmentPlans';
import { NotesInterface } from '@/types';
import AddNote from './AddNote.vue';

onMounted(() => getNotes());

const notes = ref<NotesInterface[]>([]);
const noteModalOpen = ref(false);
const treatmentPlansStore = useUserTreatmentPlansStore();
const toast = useToast();
const loadingNotes = ref(true);

watch(
  () => [treatmentPlansStore.userTreatmentPlans, treatmentPlansStore.treatmentPlanServices],
  () => getNotes(),
);

const getNotes: () => Promise<void> = async () => {
  if (can([PERMISSIONS.TREATMENT_PLANS_NOTES_INDEX])) {
    loadingNotes.value = true;
    const notesData = await treatmentPlansStore.getTreatmentPlanNotes();
    notes.value = !notesData ? [] : notesData;
    loadingNotes.value = false;
  }
};

const onAddNote = async (newNote: string) => {
  noteModalOpen.value = false;
  await treatmentPlansStore.addTreatmentPlanNote(newNote);
  toast.add({
    severity: 'success',
    summary: 'Added',
    detail: 'Note has been added',
    life: 3000,
  });
  getNotes();
};

const onCancelAdd = () => toggleNotesModal();

const toggleNotesModal = () => (noteModalOpen.value = !noteModalOpen.value);

const isLoadingNotes = computed(() => loadingNotes.value);
</script>

<template>
  <Card class="card-full-width">
    <template #header>
      <h2>Notes</h2>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.TREATMENT_PLANS_NOTES_STORE])"
          id="add_notes_btn"
          type="text"
          size="small"
          text="Add Note"
          icon-left="add"
          @click="toggleNotesModal"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        class="notes-table"
        :value="notes"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="isLoadingNotes"
        responsive-layout="scroll"
        scroll-height="440px"
        :class="{ 'table-loading': isLoadingNotes }"
      >
        <template v-if="notes.length">
          <Column field="created_at" header="Date">
            <template #body="{ data }">
              <span v-text="useDateTime().formatIntlDate(data.created_at)" />
            </template>
          </Column>
          <Column field="created_at" header="Time">
            <template #body="{ data }">
              <span v-text="useDateTime().formatLocalDateTime(data.created_at)" />
            </template>
          </Column>
          <Column field="created_by" header="Created by">
            <template #body="{ data }">
              <span v-text="data.created_by.display_name" />
            </template>
          </Column>
          <Column field="note" header="Note">
            <template #body="{ data }">
              <span class="title" v-text="data.title" />
              <span v-if="data.note" v-text="data.note" />
            </template>
          </Column>
        </template>
        <template #empty>No Notes</template>
      </DataTable>
      <AddNote
        v-if="noteModalOpen && can([PERMISSIONS.TREATMENT_PLANS_NOTES_STORE])"
        @save="onAddNote"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.p-datatable-table) {
  width: 100%;
  tbody,
  thead {
    tr {
      td,
      th {
        min-width: 12%;
        font-size: 14px;
        font-weight: 300;
        line-height: 18px;
        justify-content: flex-start;
        text-align: left;
      }

      th:last-child,
      td:last-child {
        min-width: 64%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        overflow-wrap: anywhere;
        .title {
          font-weight: 600;
        }
      }
    }
  }
}
</style>
